import useAuth from 'hooks/useAuth.ts';
import { EmailRecipients } from 'types/emails.ts';
import { FeatureFlag } from 'types/features.ts';
import { SendableInvoice } from 'types/invoices.ts';
import { combinePrimaryRecipientEmails, stringToEmailList } from 'utils/emails.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';

export const useInvoicesRecipients = (invoices: SendableInvoice[]): EmailRecipients => {
    const { user } = useAuth();
    const isInvoiceOfficeEmailEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_OFFICE_EMAIL);
    const isPrimaryRecipientSkippedIfSpecificProvided = useIsFeatureEnabled(
        FeatureFlag.PRIMARY_RECIPIENT_SKIPPED_IF_SPECIFIC_PROVIDED
    );

    const toConsolidated = new Set<string>();
    const ccConsolidated = new Set<string>();
    const bccConsolidated = new Set<string>();
    const replyToConsolidated = new Set<string>();

    for (const invoice of invoices) {
        const to = combinePrimaryRecipientEmails(
            invoice.primaryRecipientParty?.mainEmail,
            invoice.primaryRecipientAdditionalEmails,
            isPrimaryRecipientSkippedIfSpecificProvided
        );

        const secondaryRecipientMainEmails = invoice.secondaryRecipientMainEmails;

        const cc = secondaryRecipientMainEmails.length
            ? secondaryRecipientMainEmails.concat(stringToEmailList(invoice.secondaryRecipientAdditionalEmails))
            : stringToEmailList(invoice.secondaryRecipientAdditionalEmails);

        const bcc = stringToEmailList(invoice.invoiceArchiveEmails);

        const replyTo =
            isInvoiceOfficeEmailEnabled && invoice.invoicedByOffice.invoicingEmailAddress
                ? [invoice.invoicedByOffice.invoicingEmailAddress]
                : [user?.workEmailAddress];

        to.forEach((email) => {
            if (email) {
                toConsolidated.add(email);
            }
        });
        cc?.forEach((email) => {
            if (email) {
                ccConsolidated.add(email);
            }
        });
        bcc?.forEach((email) => {
            if (email) {
                bccConsolidated.add(email);
            }
        });
        replyTo?.forEach((email) => {
            if (email) {
                replyToConsolidated.add(email);
            }
        });
    }

    return {
        to: [...toConsolidated],
        cc: [...ccConsolidated],
        bcc: [...bccConsolidated],
        replyTo: [...replyToConsolidated],
    };
};
